import { fetchWithHandling } from "utils/common-utils";
import { CreateEmployeeRequest, Employee, EmployeeWithUserInfo, Filters, PagedEmployees, UpdateEmployeeRequest } from "utils/interfaces";

const API_URL = `${process.env.REACT_APP_BACKEND_API_PREFIX}/api/v1/employees`;

export const getAllPaginated = async (
    jwtToken: string,
    onSessionExpired: () => void,
    pageNumber: number = 0,
    pageSize: number = 10,
    filters: Filters<any> = {}
): Promise<PagedEmployees | undefined> => {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('pageNumber', pageNumber.toString());
        queryParams.append('pageSize', pageSize.toString());

        if (filters.name) {
            queryParams.append('name', filters.name);
        }
        if (filters.email) {
            queryParams.append('email', filters.email);
        }
        if (filters.phoneNumber) {
            queryParams.append('phoneNumber', filters.phoneNumber);
        }

        const url = `${API_URL}?${queryParams.toString()}`;

        const jsonData = await fetchWithHandling(
            url,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );

        return jsonData as PagedEmployees;
    } catch (error) {
        console.error('Error fetching employees:', error);
        return undefined;
    }
};

export const getById = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number
): Promise<Employee | undefined> => {
    try {
        const jsonData = await fetchWithHandling(
            `${API_URL}/${id}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );

        return jsonData as Employee;
    } catch (error) {
        console.error(`Error fetching employee ${id}:`, error);
        return undefined;
    }
};

// Bypasses fetchWithHandling on purpose for the 400 case: that helper already fires its own
// generic "Si è verificato un errore" toast on any 4xx before the caller gets a chance to react,
// which is why a duplicate-email create/update used to show that toast AND the page's own generic
// fallback toast, with neither saying what actually went wrong. The backend now sends the real
// message in the body for this case (EmployeeController.handleBadRequest) — this reads it directly
// so the caller can show exactly one, meaningful toast.
export type EmployeeSaveResult =
    | { status: 'success'; employee: Employee }
    | { status: 'validation_error'; message: string }
    | { status: 'error' };

const submitEmployee = async (
    url: string,
    method: 'POST' | 'PUT',
    jwtToken: string,
    onSessionExpired: () => void,
    data: CreateEmployeeRequest | UpdateEmployeeRequest
): Promise<EmployeeSaveResult> => {
    try {
        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwtToken}`,
            },
            body: JSON.stringify(data),
        });

        if (response.status === 401) {
            onSessionExpired();
            return { status: 'error' };
        }

        if (response.status === 400) {
            const message = await response.text();
            return { status: 'validation_error', message };
        }

        if (!response.ok) {
            return { status: 'error' };
        }

        const employee = (await response.json()) as Employee;
        return { status: 'success', employee };
    } catch (error) {
        console.error('Error saving employee:', error);
        return { status: 'error' };
    }
};

export const createEmployee = (
    jwtToken: string,
    onSessionExpired: () => void,
    data: CreateEmployeeRequest
): Promise<EmployeeSaveResult> =>
    submitEmployee(`${API_URL}/admin/create`, 'POST', jwtToken, onSessionExpired, data);

export const updateEmployee = (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number,
    data: UpdateEmployeeRequest
): Promise<EmployeeSaveResult> =>
    submitEmployee(`${API_URL}/${id}`, 'PUT', jwtToken, onSessionExpired, data);

export const deleteEmployee = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number
): Promise<boolean> => {
    try {
        await fetchWithHandling(
            `${API_URL}/${id}`,
            {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );

        return true;
    } catch (error) {
        console.error(`Error deleting employee ${id}:`, error);
        return false;
    }
};

export const getMyProfile = async (
    jwtToken: string,
    onSessionExpired: () => void
): Promise<EmployeeWithUserInfo | undefined> => {
    try {
        const jsonData = await fetchWithHandling(
            `${API_URL}/me/profile`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );

        return jsonData as EmployeeWithUserInfo;
    } catch (error) {
        console.error('Error fetching own employee profile:', error);
        return undefined;
    }
};

export const downloadSignatureFile = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number
): Promise<Blob | undefined> => {
    try {
        const response = await fetchWithHandling(
            `${API_URL}/${id}/signature/download`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            },
            true
        );

        if (response instanceof Response) {
            return await response.blob();
        }

        return undefined;
    } catch (error) {
        console.error(`Error downloading signature file for employee ${id}:`, error);
        return undefined;
    }
};

export const saveSignature = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number,
    signatureDataUrl: string
): Promise<Employee | undefined> => {
    try {
        const jsonData = await fetchWithHandling(
            `${API_URL}/${id}/signature`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
                body: JSON.stringify({ signatureDataUrl }),
            },
            () => {
                onSessionExpired();
            }
        );

        return jsonData as Employee;
    } catch (error) {
        console.error(`Error saving signature for employee ${id}:`, error);
        return undefined;
    }
};

export const resetPassword = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number
): Promise<boolean> => {
    try {
        const result = await fetchWithHandling(
            `${API_URL}/${id}/reset-password`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );
        return result !== null && result !== undefined;
    } catch (error) {
        console.error(`Error resetting password for employee ${id}:`, error);
        return false;
    }
};

export const deleteSignature = async (
    jwtToken: string,
    onSessionExpired: () => void,
    id: number
): Promise<boolean> => {
    try {
        await fetchWithHandling(
            `${API_URL}/${id}/signature`,
            {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            },
            () => {
                onSessionExpired();
            }
        );

        return true;
    } catch (error) {
        console.error(`Error deleting signature for employee ${id}:`, error);
        return false;
    }
};
